/**
 * Sponsor tier picker — first step of the sponsorship sub-flow.
 *
 * Public, no auth. Lists the event's publicly-purchasable sponsorship
 * tiers. Tapping a tier stashes it in FlowContext and routes to the
 * sponsor-details form.
 *
 * A single available tier skips this step: there's nothing to choose,
 * so we auto-select it and advance straight to the details form. A lone
 * sold-out tier is NOT skipped — the page renders so the sponsor sees
 * the "Sold out" state instead of being dropped into a dead-end form.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { EmbedClient, SponsorshipTier } from '@/api/embed';
import { useFlow } from '@/state/FlowContext';
import { useFlowNavigate } from '@/state/useFlowNavigate';
import './SponsorTierPage.css';

interface Props {
  client: EmbedClient;
}

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: cents % 100 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

export function SponsorTierPage({ client }: Props) {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useFlowNavigate();
  const { selectSponsorship } = useFlow();

  const [tiers, setTiers] = useState<SponsorshipTier[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const autoPickedRef = useRef(false);

  useEffect(() => {
    if (!eventId) return;
    client
      .getSponsorships(eventId)
      .then(setTiers)
      .catch((err) => {
        setError(
          err?.response?.status === 404
            ? 'Sponsorships are not available through this link.'
            : 'Could not load sponsorships. Please try again.',
        );
      });
  }, [client, eventId]);

  const onPick = useCallback(
    (tier: SponsorshipTier, replace = false) => {
      selectSponsorship(tier);
      navigate(
        `/events/${eventId}/sponsor/${tier.id}`,
        replace ? { replace: true } : undefined,
      );
    },
    [eventId, navigate, selectSponsorship],
  );

  // A single available tier has nothing to choose — auto-select it and
  // advance. `replace: true` keeps this vestigial step out of history so
  // Back returns to the product chooser. Skipped for a lone sold-out tier
  // so the sponsor still sees the "Sold out" state. Ref-guarded to fire
  // once even under StrictMode's double-invoked effects.
  useEffect(() => {
    if (!tiers || tiers.length !== 1 || autoPickedRef.current) return;
    if (tiers[0].quantity_remaining <= 0) return;
    autoPickedRef.current = true;
    onPick(tiers[0], true);
  }, [tiers, onPick]);

  if (error) {
    return <div role="alert" className="tn-sponsor__error">{error}</div>;
  }

  if (!tiers) {
    return <div className="tn-sponsor__loading">Loading sponsorships…</div>;
  }

  // Auto-skip in flight for a single available tier — render the loading
  // placeholder rather than flash a one-item list for a frame.
  if (tiers.length === 1 && tiers[0].quantity_remaining > 0) {
    return <div className="tn-sponsor__loading">Loading…</div>;
  }

  if (tiers.length === 0) {
    return (
      <div className="tn-card tn-sponsor__empty">
        <h2>No sponsorships available</h2>
        <p>The organizer hasn't opened any sponsorships for public purchase yet.</p>
      </div>
    );
  }

  return (
    <div className="tn-sponsor">
      <h1 className="tn-sponsor__title">Become a sponsor</h1>
      <p className="tn-sponsor__subtitle">Choose a sponsorship level.</p>

      <ul className="tn-sponsor__tiers">
        {tiers.map((t) => {
          const soldOut = t.quantity_remaining <= 0;
          return (
            <li key={t.id}>
              <button
                type="button"
                className={`tn-sponsor__tier${soldOut ? ' tn-sponsor__tier--sold-out' : ''}`}
                onClick={() => onPick(t)}
                disabled={soldOut}
              >
                <div className="tn-sponsor__tier-head">
                  <span className="tn-sponsor__tier-name">{t.tier_name}</span>
                  <span className="tn-sponsor__tier-price">{money(t.price_cents)}</span>
                </div>
                {t.description ? (
                  <p className="tn-sponsor__tier-desc">{t.description}</p>
                ) : null}
                {t.whats_included.length > 0 ? (
                  <ul className="tn-sponsor__tier-includes">
                    {t.whats_included.map((inc, i) => (
                      <li key={i}>
                        <span className="tn-sponsor__tier-check" aria-hidden="true">✓</span>
                        <span>{inc}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
                <div className="tn-sponsor__tier-meta">
                  {soldOut
                    ? <span className="tn-sponsor__tier-soldout">Sold out</span>
                    : `${t.quantity_remaining} available${t.requires_approval ? ' · approval required' : ''}`}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
